/**
 * Floating-label form fields (AirBnB pattern).
 *
 * The label sits *inside* the field border at rest, acting as the placeholder.
 * When the field is focused or holds a value, the label animates up to the top
 * of the field and shrinks. This is the shared chrome (`FloatingField`) plus thin
 * value-aware wrappers around the native controls (`FloatingInput`,
 * `FloatingSelect`, `FloatingTextarea`).
 *
 * Focus is detected purely in CSS via `:focus-within`, so the wrapper never needs
 * to own focus state. Whether the control holds a value is passed in as `filled`
 * (recomputed on every render) so the label floats correctly even when a
 * controlled value arrives after mount (e.g. profile data loading in late).
 *
 * @example
 * <FloatingInput label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
 * <FloatingSelect label="Country" value={code} onValueChange={setCode}>
 *   <SelectItem value="us">United States</SelectItem>
 * </FloatingSelect>
 */

import type { IconName } from './MaterialIcon.js';

import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from './MaterialIcon.js';

export interface FloatingFieldProps {
  /** The label rendered inside the field at rest, floating up on focus/value. */
  label: string;
  /** `id` of the wrapped control, so clicking the label focuses it. */
  htmlFor?: string;
  /** Whether the wrapped control currently holds a value. Drives the floated state alongside focus. */
  filled: boolean;
  /**
   * Keep the label permanently in the floated (top) position. Used by controls
   * that render their own visible content even when empty (e.g. native date inputs).
   */
  forceFloat?: boolean;
  /** Position the resting label near the top instead of centered (for multiline controls). */
  multiline?: boolean;
  /**
   * Visually hide the label (it stays in the DOM for screen readers). For fields whose purpose the
   * surrounding context already states, e.g. a dialog titled "Verify your phone number".
   */
  hideLabel?: boolean;
  required?: boolean;
  /** When set, shows the field in its error state and renders the message beneath. */
  error?: string;
  disabled?: boolean;
  /** Classes applied to the bordered field container. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Presentational floating-label container. Renders the bordered box, the floating
 * label and an optional error message. The actual control is passed as `children`
 * and must be borderless/transparent (use the `floatingControlClassName` helper).
 */
export function FloatingField({
  label,
  htmlFor,
  filled,
  forceFloat,
  multiline,
  hideLabel,
  required,
  error,
  disabled,
  className,
  children,
}: FloatingFieldProps) {
  return (
    <div className="space-y-1.5">
      <div
        data-filled={filled || forceFloat ? '' : undefined}
        className={cn(
          'group relative rounded-md border bg-background transition-colors',
          error ? 'border-destructive' : 'border-input focus-within:border-input-focus',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        <label
          htmlFor={htmlFor}
          className={cn(
            'pointer-events-none absolute left-3 z-10 origin-top-left text-base text-muted-foreground transition-all duration-150 ease-out',
            // Resting position: acts as the placeholder.
            multiline ? 'top-4' : 'top-1/2 -translate-y-1/2',
            // Floated position when focused or holding a value. Shrink with `scale` (a transform) rather
            // than `font-size`, so the element's height stays constant and the `-translate-y-1/2` reference
            // doesn't drift mid-transition — which previously caused a bounce on the return to rest.
            'group-focus-within:top-1 group-focus-within:translate-y-0 group-focus-within:scale-75',
            'group-data-[filled]:top-1 group-data-[filled]:translate-y-0 group-data-[filled]:scale-75',
            error && 'text-destructive',
            hideLabel && 'sr-only',
          )}
        >
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
        {children}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Shared classes for the borderless control rendered inside a `FloatingField`.
 * Leaves headroom at the top so the floated label never overlaps the value.
 */
export const floatingControlClassName =
  'peer w-full rounded-md border-0 bg-transparent px-3 text-base text-foreground ring-offset-0 ' +
  'focus:outline-none focus:ring-0 disabled:cursor-not-allowed';

export interface FloatingInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  label: string;
  error?: string;
  /**
   * Keep the label floated even when empty (e.g. native date/time inputs that show their own text).
   * @example <FloatingInput type="date" forceFloat label="Date of birth" value={dob} onChange={…} />
   */
  forceFloat?: boolean;
  /** Visually hide the label and center the value, when the surrounding context already names the field. */
  hideLabel?: boolean;
  /** Classes applied to the field container. */
  containerClassName?: string;
}

/** Floating-label `<input>`. Computes `filled` from its value/defaultValue. */
export const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, error, required, forceFloat, hideLabel, id, className, containerClassName, disabled, ...props }, ref) => {
    const filled = hasValue(props.value, props.defaultValue);
    return (
      <FloatingField
        label={label}
        htmlFor={id}
        filled={filled}
        forceFloat={forceFloat}
        hideLabel={hideLabel}
        required={required}
        error={error}
        disabled={disabled}
        className={containerClassName}
      >
        <input
          ref={ref}
          id={id}
          required={required}
          disabled={disabled}
          className={cn(floatingControlClassName, 'h-12', hideLabel ? 'py-0' : 'pt-5 pb-1', className)}
          {...props}
        />
      </FloatingField>
    );
  },
);
FloatingInput.displayName = 'FloatingInput';

export interface FloatingTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'> {
  label: string;
  error?: string;
  containerClassName?: string;
}

/** Floating-label `<textarea>`. */
export const FloatingTextarea = React.forwardRef<HTMLTextAreaElement, FloatingTextareaProps>(
  ({ label, error, required, id, className, containerClassName, disabled, rows = 3, ...props }, ref) => {
    const filled = hasValue(props.value, props.defaultValue);
    return (
      <FloatingField
        label={label}
        htmlFor={id}
        filled={filled}
        multiline
        required={required}
        error={error}
        disabled={disabled}
        className={containerClassName}
      >
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          required={required}
          disabled={disabled}
          className={cn(floatingControlClassName, 'pt-6 pb-2 resize-none', className)}
          {...props}
        />
      </FloatingField>
    );
  },
);
FloatingTextarea.displayName = 'FloatingTextarea';

export interface FloatingSelectProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  containerClassName?: string;
  /** Optional Material icon rendered inside the trigger on the left. */
  leadingIcon?: IconName;
  children?: React.ReactNode;
}

/**
 * Floating-label native `<select>`. The label doubles as the placeholder, so at
 * rest the select renders blank (an empty hidden option) and the label sits over it.
 *
 * Empty string is reserved for the unselected state — callers must NOT pass a
 * `<SelectItem value="">` option, or that selection would read as "filled" (label
 * floated) while appearing blank. Use a truthy value (e.g. `"none"`) for an
 * explicit "no preference" choice.
 */
export const FloatingSelect = React.forwardRef<HTMLSelectElement, FloatingSelectProps>(
  (
    {
      label,
      value,
      onValueChange,
      error,
      required,
      disabled,
      id,
      className,
      containerClassName,
      leadingIcon,
      children,
    },
    ref,
  ) => {
    return (
      <FloatingField
        label={label}
        htmlFor={id}
        filled={value !== ''}
        required={required}
        error={error}
        disabled={disabled}
        // Pin the height on the container and let the <select> fill it (h-full below). Safari sizes a
        // native <select> from its (empty) selected option, rendering it too tall until an option is
        // picked; driving height from a definite-height parent is engine-independent. `box-content`
        // keeps the inner height equal to the input's `h-12` while the 1px border sits outside it, so
        // a select matches a text field exactly (border-box would make it 2px shorter). overflow-hidden
        // guards against any residual native intrinsic overflow.
        className={cn('box-content h-12 overflow-hidden', containerClassName)}
      >
        <select
          ref={ref}
          id={id}
          required={required}
          disabled={disabled}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className={cn(
            floatingControlClassName,
            'h-full appearance-none truncate pt-5 pb-1 pr-9',
            leadingIcon ? 'pl-9' : 'pl-3',
            className,
          )}
        >
          {/* Empty, hidden placeholder so the label (not option text) shows at rest. */}
          <option value="" hidden />
          {children}
        </select>
        {leadingIcon && (
          <MaterialIcon
            icon={leadingIcon}
            size={IconSizes.sm}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        )}
        <MaterialIcon
          icon="expand_more"
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 opacity-50"
        />
      </FloatingField>
    );
  },
);
FloatingSelect.displayName = 'FloatingSelect';

/** True when a controlled value or defaultValue is meaningfully non-empty. */
function hasValue(value: React.ReactNode, defaultValue: React.ReactNode): boolean {
  const v = value ?? defaultValue;
  if (v === undefined || v === null) return false;
  return String(v).length > 0;
}
